import { ColumnDef } from '@tanstack/react-table';
import { SourcingOpenings } from 'api-clients/monolith';
import _isEmpty from 'lodash/isEmpty';
import _round from 'lodash/round';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { PerformanceStatusPill } from '@/components/base/performance-status-pill';
import { SourcingStatusPill } from '@/components/base/sourcing-status-pill';
import { LightningBoltIcon } from '@/components/icons/lightning-bolt';
import { BUDGET_THRESHOLD_PERCENTAGE } from '@/components/pages/channels/automation-status-banner/constants';
import { Button } from '@/components/ui/button';
import { ChannelIcon } from '@/components/ui/channel-icon';
import { Checkbox } from '@/components/ui/checkbox';
import { ConversionStats } from '@/components/ui/conversion-stats';
import { CurrencyStats } from '@/components/ui/currency-stats';
import { Duration } from '@/components/ui/duration';
import { HireStats } from '@/components/ui/hire-stats';
import { OpeningStatus } from '@/components/ui/opening-status';
import { Tooltip } from '@/components/ui/tooltip';
import { Typography } from '@/components/ui/typography';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { useNoHiringGoalDialog } from '@/contexts/no-hiring-goal-dialog-context';
import { formatShortDate } from '@/utils/date-util';
import { parseCurrency } from '@/utils/parse-currency';

import { Menu } from './menu';
import { useEditTargets } from './use-edit-targets';

export function useDashboardColumns(): ColumnDef<SourcingOpenings>[] {
  const intl = useIntl();
  const { handleEditTargets } = useEditTargets();
  const { openNoHiringGoalDialog } = useNoHiringGoalDialog();
  const navigate = useNavigate();
  const { setOpenChannelsDialog, setSelectedOpeningIds } =
    useJobPostingContext();

  const handleNoHiringGoal = (rowItem: SourcingOpenings) => {
    return openNoHiringGoalDialog({
      title: rowItem.title,
      brandSlug: rowItem.brand_slug,
      locationId: rowItem.location_id,
      funnelId: rowItem.external_id,
    });
  };

  const handleTitleClick = (rowItem: SourcingOpenings) => {
    if (!rowItem.hiring_goal) return handleNoHiringGoal(rowItem);

    return navigate(`${rowItem.slug}/channels`);
  };

  const handleTargetsClick = (rowItem: SourcingOpenings) => {
    if (!rowItem.hiring_goal) return handleNoHiringGoal(rowItem);

    return handleEditTargets(rowItem);
  };

  const handlePostJobClick = (funnelId: string) => {
    setSelectedOpeningIds([funnelId]);
    setOpenChannelsDialog(true);
  };

  return [
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected()
              ? true
              : table.getIsSomePageRowsSelected()
              ? 'indeterminate'
              : false
          }
          onCheckedChange={value => table.toggleAllPageRowsSelected(!!value)}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={value => row.toggleSelected(!!value)}
        />
      ),
    },
    {
      accessorKey: 'title',
      header: () => <FormattedMessage defaultMessage="Opening name" />,
      cell: ({ row }) => {
        return (
          <div className="flex min-w-[200px] max-w-[280px] items-center gap-1 overflow-hidden">
            <OpeningStatus active={row.original.active} />
            <Button
              variant="link"
              onClick={() => handleTitleClick(row.original)}
              className="whitespace-break-spaces h-auto text-left"
            >
              {row.original.title}
            </Button>
          </div>
        );
      },
    },
    {
      accessorKey: 'performance_status_metrics',
      header: () => <FormattedMessage defaultMessage="Status" />,
      cell: ({ row }) => {
        const performanceStatusMetrics =
          row.original.performance_status_metrics;
        const { status, criteria } = performanceStatusMetrics;

        if (!status) return <span>-</span>;

        return (
          <PerformanceStatusPill
            status={status}
            criteria={criteria}
            expected={
              criteria === 'hire'
                ? performanceStatusMetrics.expected_hires_count
                : performanceStatusMetrics.expected_applicants_count
            }
            actual={
              criteria === 'hire'
                ? row.original.hire_stats.actual
                : row.original.applicants_stats.actual
            }
            startDate={formatShortDate(
              row.original.hiring_goal?.start_date ?? '',
            )}
            endDate={formatShortDate(row.original.hiring_goal?.hire_by ?? '')}
            duration={row.original.hiring_goal?.duration}
          />
        );
      },
    },
    {
      accessorKey: 'hire_stats',
      header: () => <FormattedMessage defaultMessage="Hires" />,
      cell: ({ row }) => (
        <HireStats
          stats={row.original.hire_stats}
          isHighlighted={row.original.has_cross_funnel_transitions}
          openingSlug={row.original.slug}
        />
      ),
    },
    {
      accessorKey: 'total_duration',
      header: () => <FormattedMessage defaultMessage="Duration" />,
      cell: ({ row }) => (
        <Duration
          stats={row.original.duration_stats}
          brand={row.original.brand_slug ?? 'no_brand'}
          location={row.original.location_id}
          funnel={row.original.external_id}
          showNewGoalButton={!row.original.hiring_goal?.is_current}
        />
      ),
    },
    {
      accessorKey: 'conversion_stats',
      header: () => <FormattedMessage defaultMessage="Conversion" />,
      cell: ({ row }) => (
        <ConversionStats
          stats={row.original.conversion_stats}
          onClick={() => handleTargetsClick(row.original)}
        />
      ),
    },
    {
      accessorKey: 'applicants_stats',
      header: () => <FormattedMessage defaultMessage="Applicants" />,
      cell: ({ row }) => (
        <HireStats
          stats={row.original.applicants_stats}
          openingSlug={row.original.slug}
        />
      ),
    },
    {
      accessorKey: 'budget_stats',
      header: () => <FormattedMessage defaultMessage="Budget spend" />,
      cell: ({ row }) => {
        const budget = parseCurrency(row.original.budget_stats.expected);
        const spend = row.original.spend_stats?.total;

        const hasSpendBreachedThreshold = !!(
          budget &&
          spend &&
          spend < budget &&
          spend > budget * BUDGET_THRESHOLD_PERCENTAGE
        );
        const spendPercentage = hasSpendBreachedThreshold
          ? _round((spend / budget) * 100, 1)
          : undefined;

        return (
          <div className="flex items-center gap-1">
            <CurrencyStats
              stats={row.original.budget_stats}
              onClick={
                row.original.hiring_goal?.is_current
                  ? () => handleTargetsClick(row.original)
                  : undefined
              }
              isHighlighted={row.original.has_cross_funnel_spend}
              openingSlug={row.original.slug}
            />
            {hasSpendBreachedThreshold && (
              <Tooltip
                content={intl.formatMessage(
                  {
                    defaultMessage:
                      "Budget Automation: You've used {spendPercentage}% of the budget.",
                    description: 'Tooltip content for budget automation',
                  },
                  {
                    spendPercentage:
                      spendPercentage ?? BUDGET_THRESHOLD_PERCENTAGE * 100,
                  },
                )}
              >
                <span>
                  <LightningBoltIcon className="text-amber-600" />
                </span>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'total_cpa',
      header: () => <FormattedMessage defaultMessage="CPA" />,
      cell: ({ row }) => (
        <CurrencyStats
          stats={row.original.cpa_stats}
          onClick={
            row.original.hiring_goal?.is_current
              ? () => handleTargetsClick(row.original)
              : undefined
          }
        />
      ),
    },
    {
      accessorKey: 'total_cph',
      header: () => <FormattedMessage defaultMessage="CPH" />,
      cell: ({ row }) => (
        <CurrencyStats
          stats={row.original.cph_stats}
          onClick={
            row.original.hiring_goal?.is_current
              ? () => handleTargetsClick(row.original)
              : undefined
          }
        />
      ),
    },
    {
      accessorKey: 'channels',
      header: () => <FormattedMessage defaultMessage="Channels" />,
      cell: ({ row }) => {
        const { channels } = row.original;

        if (_isEmpty(channels)) return <span>-</span>;

        return (
          <div className="flex w-max">
            {channels?.map(channel => (
              <ChannelIcon
                key={channel}
                channelSlug={channel}
                className="-mr-1 last:mr-0"
              />
            ))}
          </div>
        );
      },
    },
    {
      accessorKey: 'sourcing_status',
      header: () => <FormattedMessage defaultMessage="Sourcing" />,
      cell: ({ row }) => {
        const sourcingStatus = row.original.sourcing_status;

        if (!sourcingStatus) return <span>-</span>;

        return <SourcingStatusPill status={sourcingStatus} />;
      },
    },
    {
      accessorKey: 'brand_name',
      header: () => <FormattedMessage defaultMessage="Brand" />,
      cell: ({ row }) => (
        <Typography variant="subtitle1">{row.original.brand_name}</Typography>
      ),
    },
    {
      accessorKey: 'menu',
      header: () => null,
      cell: ({ row }) => {
        return (
          <Menu
            opening={row.original}
            onEditTargets={() => handleTargetsClick(row.original)}
            onPostJob={() => handlePostJobClick(row.original.external_id)}
          />
        );
      },
    },
  ];
}
